/**
 * Full Meet Settings bridge for the hosted-mode meeting popup.
 *
 * `buildJitsiConfigOverwrite` builds the Jitsi config from
 * `sangoma-meet-settings` on the origin it runs on. In hosted/MF mode the
 * meeting runs in a popup on NG Meet's origin, while the user's settings were
 * saved on the TeamHub host origin — so the popup read an empty settings object
 * and every toggle fell back to `jitsiDefaultSettings`. That single gap is what
 * SM-1187 (devices), SM-1188 (start muted / video off) and SM-1190 (pre-meeting
 * screen) all report.
 *
 * This module ships the host's settings across the boundary. Device selection
 * still travels as labels (ids are origin-salted — see `deviceBridge`), but the
 * meeting toggles are plain values and are copied verbatim.
 *
 * Ordering matters: prejoin and start-muted are consumed only when the Jitsi
 * iframe is created, so unlike device selection they cannot be re-applied after
 * join. The popup therefore blocks meeting initialization until this payload
 * lands (see `useMeetingPopupDeviceBridge`).
 */

import { settingsService } from '../settingsService';
import { applyDeviceLabelsToPopupStorage, getHostDeviceLabels } from './deviceBridge';
import type { MeetingWindowSettingsPayload } from './protocol';

/** Opener side: snapshot the host's settings for handoff to the popup. */
export async function getHostSettingsPayload(): Promise<MeetingWindowSettingsPayload> {
  const settings = settingsService.getSettings();
  return {
    meetings: { ...settings.meetings } as MeetingWindowSettingsPayload['meetings'],
    startWithAudioMuted: !!settings.devices.startWithAudioMuted,
    startWithVideoMuted: !!settings.devices.startWithVideoMuted,
    deviceLabels: await getHostDeviceLabels(),
  };
}

/**
 * Popup side: write the host's settings into this origin's storage so the
 * meeting config picks them up. Persists through `settingsService` so both
 * `sangoma-meet-settings` (what `buildJitsiConfigOverwrite` reads) and the
 * device keys are updated.
 */
export async function applySettingsPayloadToPopup(
  payload: MeetingWindowSettingsPayload,
): Promise<void> {
  const current = settingsService.getSettings();
  await settingsService.updateSettings(
    {
      meetings: { ...current.meetings, ...payload.meetings },
      devices: {
        ...current.devices,
        startWithAudioMuted: payload.startWithAudioMuted,
        startWithVideoMuted: payload.startWithVideoMuted,
      },
    },
    { validate: false, persist: true },
  );

  // Device ids are origin-specific, so this resolves the bridged labels against
  // the popup's own enumeration.
  await applyDeviceLabelsToPopupStorage(payload.deviceLabels);
}
